import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { apiGet } from "@/lib/api-error";
import { Check, CaretDown, MagnifyingGlass, Users } from "@phosphor-icons/react";

interface TherapistOption {
  id: string;
  userName: string;
  userImage?: string | null;
}

interface TherapistPickerProps {
  value: string | null;
  onChange: (id: string | null) => void;
  allLabel: string;
  searchPlaceholder: string;
}

export function TherapistPicker({
  value,
  onChange,
  allLabel,
  searchPlaceholder,
}: TherapistPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const { data: therapists = [] } = useQuery({
    queryKey: ["admin", "calendar", "therapists"],
    queryFn: () =>
      apiGet<TherapistOption[]>(
        "/api/therapists?status=approved",
        "Greška pri učitavanju terapeuta",
      ),
    staleTime: 1000 * 60,
  });

  const selected = useMemo(
    () => therapists.find((t) => t.id === value) ?? null,
    [therapists, value],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return therapists;
    const q = query.toLowerCase();
    return therapists.filter((t) => t.userName?.toLowerCase().includes(q));
  }, [therapists, query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "inline-flex h-10 min-w-[200px] items-center gap-2 rounded-xl border px-3.5 text-sm font-medium shadow-sm transition-colors",
            value
              ? "border-primary/30 bg-primary/[0.04] text-foreground"
              : "border-border/70 bg-card/60 text-muted-foreground hover:bg-muted/40 hover:text-foreground",
          )}
        >
          {selected ? (
            <>
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                {selected.userName?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <span className="flex-1 truncate text-left">{selected.userName}</span>
            </>
          ) : (
            <>
              <Users className="size-4 shrink-0" />
              <span className="flex-1 truncate text-left">{allLabel}</span>
            </>
          )}
          <CaretDown className="size-4 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <div className="border-b border-border/40 p-2">
          <div className="relative">
            <MagnifyingGlass className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 pl-8 text-xs"
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          <button
            onClick={() => {
              onChange(null);
              setOpen(false);
              setQuery("");
            }}
            className={cn(
              "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors",
              value === null
                ? "bg-primary/[0.06] text-foreground"
                : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
            )}
          >
            <div className="flex size-6 items-center justify-center rounded-full bg-muted/60">
              <Users className="size-3 text-muted-foreground" />
            </div>
            <span className="flex-1 font-medium">{allLabel}</span>
            {value === null && <Check className="size-3.5 text-primary" />}
          </button>

          <div className="my-1 border-t border-border/30" />

          {filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">
              —
            </p>
          ) : (
            filtered.map((t) => {
              const active = t.id === value;
              return (
                <button
                  key={t.id}
                  onClick={() => {
                    onChange(t.id);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors",
                    active
                      ? "bg-primary/[0.06] text-foreground"
                      : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
                  )}
                >
                  <div className="flex size-6 items-center justify-center rounded-full bg-primary/10 text-[9px] font-bold text-primary">
                    {t.userName?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <span className="flex-1 truncate font-medium">{t.userName}</span>
                  {active && <Check className="size-3.5 shrink-0 text-primary" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
